<script setup lang="ts">
import { useFormat } from '@/composables/format';
import { useGrid } from '@/composables/grid';
import { useLocale } from '@/composables/locale';
import { safeParseNumber } from '@/composables/utils';
import { GridConfig } from '@/models/grid-config';
import { GridNames } from '@/models/grids-names.enum';
import router from '@/router';
import { useSelectedOrdersStore } from '@/stores/selected-grid-orders';
import { useUserStore } from '@/stores/user';
import { ClientSideRowModelModule } from '@ag-grid-community/client-side-row-model';
import { GridApi, type GridOptions } from '@ag-grid-community/core';
import { AgGridVue } from '@ag-grid-community/vue3';
import { DraftModel, DraftProductModel, OrderStatus } from '@onrunning/backstage-models';
import { storeToRefs } from 'pinia';
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { DraftsService } from '../../services/drafts.service';
import { useGridsStore } from '../../stores/grids';

export interface OrderRow {
  id: string;
  orderNumber: string;
  dueDate: Date;
  quantityToShip: number;
  status: string;
  itemShipped: number;
  currencyCode: string;
  total?: number;
  quantitiesPerStatus?: { _id: string; quantity: number };
}

export interface Props {
  enablePagination: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  enablePagination: true,
});
const emits = defineEmits(['gridLoadingDone']);

const { selectedAccountId } = storeToRefs(useUserStore());
const { userLocale } = useLocale();
const { addEditGridConfig, getGridByName } = useGridsStore();
const { defaultGridOptions, applyGridConfig, dateColumnDefinition, numberColumnDefinition } = useGrid();
const { formatCurrency, currencyComparator } = useFormat();
const { getSelectedOrders } = useSelectedOrdersStore();
const { t } = useI18n();

const loading = ref(true);
const columnDefinitions = ref(new Array<any>());
const rowClassRules = ref(undefined as any);
const defaultColDef = ref({
  resizable: true,
  filter: true,
  sortable: true,
  minWidth: 80,
});
const reloadGrid = ref(0);
const gridName = GridNames.OrderDuplicationHeaders;
const pageSize = 50;
const gridApi = ref(undefined as GridApi | undefined);
const modules = [ClientSideRowModelModule];

const initialGridConfig = ref<GridConfig | undefined>();
const gridOptions = {
  ...defaultGridOptions(),
  getRowClass: (params: any) => {
    if (params.node.data.status === OrderStatus.NotSubmitted) {
      return 'draft-highlight';
    }
  },
  suppressPaginationPanel: !props.enablePagination,
  rowSelection: 'multiple',
  rowMultiSelectWithClick: true,
} as GridOptions<any>;

const draftsService = new DraftsService();
const draftsWithPrices = ref(new Array<DraftModel>());

watch(
  () => selectedAccountId.value,
  async (newVal) => {
    if (newVal) {
      router.push('/orders');
    }
  },
);

watch(
  () => getSelectedOrders(),
  async (newVal) => {
    if (newVal.length === 0) {
      router.push('/orders');
    }
  },
);

watch(
  () => userLocale.value,
  async (newVal) => {
    if (newVal) {
      reloadGrid.value++;
    }
  },
);

onBeforeUnmount(() => {
  gridApi.value = undefined;
});

onMounted(async () => {
  draftsWithPrices.value = await fetchDraftsIfAny();
  initialGridConfig.value = getGridByName(gridName);
  gridOptions.onPaginationChanged = onPaginationChanged;
  initializeGrid();

  loading.value = false;
  emits('gridLoadingDone');
});

const rowData: Array<OrderRow> = getSelectedOrders().map((order) => {
  order.dueDate = new Date(order.dueDate);
  const { id, orderNumber, dueDate, quantityToShip, total, status, quantitiesPerStatus, itemShipped, currencyCode } =
    order;

  return {
    id,
    orderNumber,
    dueDate,
    quantityToShip,
    total,
    status,
    quantitiesPerStatus,
    itemShipped,
    currencyCode,
  };
});

async function fetchDraftsIfAny(): Promise<DraftModel[]> {
  const result = new Array<DraftModel>();
  const draftIds = getSelectedOrders()
    .filter((selectedOrder) => selectedOrder.orderNumber.startsWith('DRAFT'))
    .map((selectedOrder) => selectedOrder.id);

  await Promise.all(
    draftIds.map(async (draftId) => {
      const validatorResult = await draftsService.getPrices(draftId, selectedAccountId.value);

      if (validatorResult.isValid() && validatorResult.model) {
        result.push(validatorResult?.model);
      }
    }),
  );

  return result;
}
function calculateDraftQuantity(draft: DraftModel) {
  return draft?.products.reduce(
    (accumulator: number, product: DraftProductModel) => accumulator + safeParseNumber(product?.quantity),
    0,
  );
}

function calculateDraftPrice(draft: DraftModel) {
  return draft?.products.reduce(
    (accumulator: number, product: DraftProductModel) => accumulator + safeParseNumber(product?.totalPrice),
    0,
  );
}

function calculateTotalPrice(params: any): number {
  if (params.data.status === OrderStatus.NotSubmitted && draftsWithPrices.value?.length > 0) {
    const foundDraft = draftsWithPrices.value.find((draft) => draft.id === params.data.id);

    if (foundDraft) {
      return foundDraft?.total ? safeParseNumber(foundDraft?.total) : safeParseNumber(calculateDraftPrice(foundDraft));
    }

    return 0;
  }

  return safeParseNumber(params.data?.total);
}

function onPaginationChanged(gridOptions: { api: GridApi }) {
  if (gridOptions.api) {
    addEditGridConfig({
      name: gridName,
      pageNumber: gridOptions.api.paginationGetCurrentPage(),
    });
  }
}

function calculateTotalOrderedItems(params: any) {
  if (params.data.status === OrderStatus.NotSubmitted && draftsWithPrices.value) {
    const foundDraft = draftsWithPrices.value.find((draft) => draft.id === params.data.id) as DraftModel;

    const draftQuantity = calculateDraftQuantity(foundDraft);

    return draftQuantity;
  }

  // for orders from dynamics
  if (params.data.quantitiesPerStatus) {
    return params.data.quantitiesPerStatus.reduce(
      (accumulator: number, item: { quantity: number }) => accumulator + safeParseNumber(item.quantity),
      0,
    );
  }

  return safeParseNumber(params.data.itemShipped) + safeParseNumber(params.data.quantityToShip);
}

function currencyFormatter(params: any) {
  return formatCurrency(params.data.currencyCode, params.value);
}

function initializeGrid() {
  columnDefinitions.value = [
    {
      headerValueGetter: () => t('viewOrders.orders_list_columns.sales_order_number'),
      field: 'orderNumber',
      suppressSizeToFit: true,
      width: 126,
      showDisabledCheckboxes: true,
    },
    dateColumnDefinition({
      headerValueGetter: () => t('viewOrders.orders_list_columns.ship_date'),
      field: 'dueDate',
    }),
    {
      headerValueGetter: () => t('viewOrders.orders_list_columns.order_total'),
      field: 'total',
      cellStyle: { textAlign: 'right' },
      valueGetter: (params: any) => {
        return `${calculateTotalPrice(params)}`;
      },
      valueFormatter: currencyFormatter,
      comparator: currencyComparator,
    },
    numberColumnDefinition({
      headerValueGetter: () => t('viewOrders.orders_list_columns.quantity_total'),
      field: 'quantity',
      valueGetter: (params: any) => {
        return `${calculateTotalOrderedItems(params)}`;
      },
    }),
  ];

  gridApi.value?.setGridOption('columnDefs', columnDefinitions.value);

  rowClassRules.value = {
    'gray-row': (params: any) => params.data.status === OrderStatus.Hold,
  };
}

function onGridReady(params: any) {
  gridApi.value = params.api;

  if (initialGridConfig.value) {
    applyGridConfig(initialGridConfig.value, gridApi.value as GridApi);
  }
}

function onFirstDataRendered() {
  gridApi.value?.sizeColumnsToFit();
}
</script>

<template>
  <div class="orders-grid">
    <div v-if="loading" class="loader"></div>
    <div v-else>
      <div v-if="getSelectedOrders().length > 0" class="form grid-cnt">
        <AgGridVue
          class="ag-theme-balham"
          :grid-options="gridOptions"
          :column-defs="columnDefinitions"
          :row-data="rowData"
          :default-col-def="defaultColDef"
          :modules="modules"
          :pagination="enablePagination"
          :pagination-page-size="pageSize"
          :suppress-menu-hide="true"
          :row-class-rules="rowClassRules"
          v-on:first-data-rendered="onFirstDataRendered"
          v-on:grid-ready="onGridReady">
        </AgGridVue>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.orders-grid {
  .draft-highlight {
    background: var(--row-highlight-color) !important;
  }

  .hold {
    color: var(--special-red-color-darker);
  }

  .gray-row {
    color: var(--light-gray-color);
  }

  .ag-theme-balham {
    width: 100%;
    height: 100%;
  }

  .grid-cnt:deep() {
    padding: 0;
    border-top: 1px var(--gray-color)-border solid;
    border-bottom: 1px var(--gray-color)-border solid;
  }
}
</style>
